import moment from "moment"

interface CurrencyInput {
  cents: number
  currency_iso: string
}

type DateFormat = "default" | "short" | "long" | "iso"

const DATE_FORMATS = {
  default: "MMM Do YYYY", // Oct 31st 2024
  short: "MM/DD/YY", // 10/31/24
  long: "MMMM Do, YYYY", // October 31st, 2024
  iso: "YYYY-MM-DD", // 2024-10-31
}

/**
 * Formats a currency amount from cents to a localized string
 * @param {CurrencyInput} input - Object containing cents and currency_iso
 * @returns {string} Formatted currency string
 * @example
 * formatCurrency({ cents: 1234567890, currency_iso: "USD" })
 * // Returns "$12,345,678.90"
 */
export const formatCurrency = (input?: CurrencyInput | null): string => {
  if (!input) return ""
  const { cents, currency_iso = "USD" } = input

  const amount = cents / 100

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency_iso,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Formats a cents amount to a localized string
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted currency string
 * @example
 * formatCurrencyCents(1234567890)
 * // Returns "$12,345,678.90"
 */
export const formatCurrencyCents = (cents: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100)
}

/**
 * Formats a date string or object to a consistent format
 * @param {string | Date} date - Date to format
 * @param {DateFormat} [format="default"] - Predefined format to use
 * @returns {string} Formatted date string
 * @example
 * formatDate("2024-10-31")
 * // Returns "Oct 31st 2024"
 */
export const formatDate = (date: string | Date, format: DateFormat = "default"): string => {
  const momentDate = moment(date)

  if (!momentDate.isValid()) {
    throw new Error("Invalid date provided")
  }

  return momentDate.format(DATE_FORMATS[format])
}

export const singularize = (word: string): string => {
  if (word.endsWith("ies")) {
    return word.slice(0, -3) + "y"
  }
  if (word.endsWith("es")) {
    return word.slice(0, -2)
  }
  if (word.endsWith("s")) {
    return word.slice(0, -1)
  }
  return word
}

/**
 * Capitalizes the first letter of a word
 * @param {string} word - Word to capitalize
 * @returns {string} Capitalized word
 * @example
 * capitalize("hello")
 * // Returns "Hello"
 */
export const capitalize = (word: string): string => {
  if (typeof word !== "string" || word.length === 0) {
    return word
  }
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/**
 * Converts a string to a human readable format
 * @param {string} str - String to convert
 * @returns {string} Human readable string
 * @example
 * humanize("foo bar baz") // Returns "Foo bar baz"
 * humanize("fooBarBaz") // Returns "Foo Bar Baz"
 * humanize("foo_bar_baz") // Returns "Foo bar baz"
 * humanize("foo-bar-baz") // Returns "Foo bar baz"
 * humanize("_foo_bar_baz_") // Returns "Foo bar baz"
 */
export const humanize = (str: string): string => {
  if (!str) return ""
  return str
    .replace(/^[_\s]+|[_\s]+$/g, "")
    .replace(/[_-\s]+/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^[a-z]/, function (m) {
      return m.toUpperCase()
    })
}

/**
 * Safely parses the ID from a route param string.
 * Expected format: "prefix_id". Returns the ID part or empty string if invalid.
 */
export const parseRouteId = (param?: string): string => {
  if (!param) return ""
  const parts = param.split("_")
  if (parts.length < 2 || !parts[1]) return ""
  return parts[1]
}

export const titleize = (str?: string | null): string => {
  if (!str) return "—"

  return str
    .replace(/^[_\s]+|[_\s]+$/g, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → camel Case
    .replace(/[_-\s]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase())
}
